import { Block, Blocks } from '@chameleoncreator/creator-app';
import { getTheme, TTheme } from './theme';

export enum TrackerState {
  DIRTY,
  CLEAN,
  DELETED,
}

export interface Tracker {
  [key: string]: TrackerState;
}

interface Timing {
  firstLoad: number;
  complete: number;
  buttonClick: { [key: string]: number };
}

export interface Report {
  key?: number;
  id: string;
  question?: string | undefined;
  reportable: 'multichoice' | 'sortation' | 'likert' | 'fillin' | 'longfillin';
  type: string;
  response: string;
  correct?: string;
  result: 0 | 1;
}

export interface AssessmentData {
  maxAttempts: number;
  scoring: boolean;
  score: number;
  passMark: number;
  passed: boolean;
}

export interface QuestionData {
  id: string;
  questionName: string;
  answer?: string[];
  correctAnswer: string[];
  isCorrect?: boolean | undefined;
  attemptCount?: string | number;
  navigateToResults?: boolean;
  isLocked?: boolean;
  submitButtonLabel?: string;
  hasNotBeenAttempted?: boolean | undefined;
}

export interface Question {
  [key: string]: QuestionData;
}

export interface Assessment {
  [key: string]: {
    data?: AssessmentData;
    results?: {
      [key: string]: Question;
    };
  };
}

export interface Assessments {
  [key: string]: Assessment;
}

export type Audio = {
  id?: string;
  src: string;
  audioSrc: string;
  autoPlay?: boolean;
  controls?: boolean;
  delay?: number;
  pause?: boolean;
} | null;

export type IdSIdMapping = {
  idToSId: { [key: string]: Number };
  sIdToId: { [key: string]: string };
};

export type LessonStatus = 'completed/incomplete' | 'passed/failed/incomplete';

export type LandingPagesSeenState = {
  [sId: number | string]: number[];
};

export interface NextState {
  apiError: { count: number; err: any };
  blocks: { [key: string]: Block };
  bookmarkLoaded: boolean;
  bookmarkLocation: string | undefined;
  currentInteraction?: string;
  currentInteractionBlockId?: string | null;
  currentTopic?: string;
  audio?: Audio;

  audioAcknowledged: boolean;
  customFonts: any[];
  error: any;
  exit: boolean;
  exitComplete: boolean;
  moduleWasManuallySetComplete: undefined | boolean;
  feedback: {
    [key: string]: {
      key: string;
      rating: number;
      message: string;
      identity: string;
      reference: string;
    };
  };
  focusedNodeId?: string;
  hasError: boolean;
  hideHeader: boolean;
  header?: Block;
  standardHeader?: Block;
  graphicHeader?: Block;
  standardHeaderLogo?: Block;
  graphicHeaderBackground?: Block;
  graphicHeaderLogo?: Block;
  interactionsLocked: string[];
  interactionsOpen: string[];
  interactionsSeen: string[];
  landingPagesSeenState: LandingPagesSeenState;
  isScrolling: boolean;
  lastFocusedNodeId?: string;
  links: any;
  mirrorActive: boolean;
  idToSIdMap: { [key: string]: number };
  sIdToIdMap: { [key: string]: number };
  topicsIdSIdMapping: IdSIdMapping;
  assessmentQuestionIdSIdMapping: IdSIdMapping;
  nextInteractionMap: { [key: string]: string };
  projectId?: string;
  rootBlockId: string | undefined;
  scormSettings: {
    passMark: number;
    setCompleteFromScore: boolean;
    setCompleteOnExit: boolean;
    setCompleteWhenAllPagesViewed: boolean;
    sendOnlySuccessStatus: boolean;
    lessonStatus: LessonStatus;
  };
  theme: TTheme;
  themeId?: string;
  tracker: Tracker;
  userBehaviour: {
    interactionTiming: { [key: string]: Timing };
    score: { [key: string]: 0 | 1 };
    scormResponses: {
      [key: string]: Report;
    };
  };
  moduleVariables: { [key: string]: string };
  assessments: Assessments;
}

export type State = NextState;

export type Action = { type: string; data: { [key: string]: any } };

export const initialState: NextState = {
  apiError: { count: 0, err: undefined },
  blocks: {},
  bookmarkLoaded: false,
  bookmarkLocation: undefined,
  currentInteraction: undefined,
  currentInteractionBlockId: undefined,
  currentTopic: undefined,
  audio: null,
  audioAcknowledged: false,
  customFonts: [],
  error: undefined,
  exit: false,
  exitComplete: false,
  moduleWasManuallySetComplete: undefined,
  focusedNodeId: undefined,
  feedback: {},
  hasError: false,
  hideHeader: false,
  standardHeader: undefined,
  graphicHeader: undefined,
  standardHeaderLogo: undefined,
  graphicHeaderBackground: undefined,
  graphicHeaderLogo: undefined,
  interactionsLocked: [],
  interactionsOpen: [],
  interactionsSeen: [],
  landingPagesSeenState: {},
  isScrolling: false,
  lastFocusedNodeId: undefined,
  links: {},
  mirrorActive: false,
  idToSIdMap: {},
  sIdToIdMap: {},
  topicsIdSIdMapping: {
    idToSId: {},
    sIdToId: {},
  },
  assessmentQuestionIdSIdMapping: {
    idToSId: {},
    sIdToId: {},
  },
  nextInteractionMap: {},
  projectId: undefined,
  rootBlockId: undefined,
  scormSettings: {
    passMark: 80,
    setCompleteFromScore: false,
    setCompleteOnExit: false,
    setCompleteWhenAllPagesViewed: true,
    sendOnlySuccessStatus: false,
    lessonStatus: 'completed/incomplete',
  },
  theme: getTheme(),
  themeId: undefined,
  tracker: {},
  userBehaviour: {
    score: {},
    interactionTiming: {},
    scormResponses: {},
  },
  moduleVariables: {},
  assessments: {},
};
